import { Box, Typography } from "@mui/material";
import { ScrapEyesEvent } from "components/scrapEyeLabelling/types";
import { Dayjs } from "dayjs";
import { useMemo } from "react";
import { useScrapEyeMetadata } from "src/contexts/scrapEyeMetadata";
import { DefaultAreas } from "src/store/api/generatedApi";

export type ShortLabelDetail = "area";

interface VideoTimelineShortLabelTrackProps {
  events: ScrapEyesEvent[];
  videoStart: Dayjs;
  duration: number;
  activeEventIds: ReadonlySet<string>;
  onEventClick?: (eventId: string) => void;
  shortLabelDetail?: ShortLabelDetail;
}

const areaSuffixFor = (area: string | null, defaultAreas: DefaultAreas) =>
  area === defaultAreas.drop ? "+" : area === defaultAreas.lift ? "-" : "";

export const VideoTimelineShortLabelTrack = ({
  events,
  videoStart,
  duration,
  activeEventIds,
  onEventClick,
  shortLabelDetail,
}: VideoTimelineShortLabelTrackProps) => {
  const { default_areas: defaultAreas } = useScrapEyeMetadata();

  const eventsWithLabels = useMemo(
    () => events.filter((e) => e.shortLabel),
    [events]
  );

  const showsAreaDetail = (event: ScrapEyesEvent) =>
    shortLabelDetail === "area" &&
    event.areaShortLabel !== "" &&
    areaSuffixFor(event.area, defaultAreas) === "";

  return (
    <Box
      sx={{
        position: "relative",
        height: eventsWithLabels.some(showsAreaDetail) ? 28 : 16,
        width: "100%",
      }}
    >
      {eventsWithLabels.map((event) => {
        const startSeconds = event.start.diff(videoStart, "second");
        const startPercent = (startSeconds / duration) * 100;

        const isActive = activeEventIds.has(event.id);

        const areaSuffix = areaSuffixFor(event.area, defaultAreas);

        return (
          <Typography
            key={event.id}
            variant="caption"
            onClick={
              onEventClick
                ? () => {
                    onEventClick(event.id);
                  }
                : undefined
            }
            sx={{
              position: "absolute",
              left: `${startPercent}%`,
              transform: "translateX(-1px)",
              fontWeight: "bold",
              color: isActive ? "primary.main" : "text.secondary",
              opacity: event.opacity,
              fontSize: 11,
              whiteSpace: "nowrap",
              ...(onEventClick
                ? {
                    cursor: "pointer",
                    transition: "transform 0.2s, color 0.2s",
                    // A one-character glyph is too narrow to hit reliably, so
                    // pad the target sideways only: the bars above and the
                    // seek slider below would swallow a taller one.
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      bottom: 0,
                      left: -4,
                      right: -4,
                    },
                    "&:hover": {
                      color: isActive ? "primary.main" : "text.primary",
                      transform: "translateX(-1px) scale(1.2)",
                    },
                  }
                : {}),
            }}
          >
            {event.shortLabel}
            {areaSuffix}
            {showsAreaDetail(event) ? (
              <Box
                component="span"
                sx={{
                  display: "block",
                  fontStyle: "italic",
                  fontWeight: "fontWeightRegular",
                  fontSize: 9,
                  lineHeight: 1,
                }}
              >
                {event.areaShortLabel}
              </Box>
            ) : null}
          </Typography>
        );
      })}
    </Box>
  );
};
